import { useCallback, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useContent } from 'src/contexts/ContentContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { downloadFileFromUrl } from 'src/utils/fileUtils';
import {
  AccountingEventClient,
  AccountingEventExportResult,
  Feature,
} from 'src/WebApiController';

export interface UseAccountingEventExportParams {
  stream: string;
  createDateFrom?: string;
  createDateTo?: string;
  effectiveDateFrom?: string;
  effectiveDateTo?: string;
  entityId?: number;
}

export const useAccountingEventExport = ({
  stream,
  createDateFrom,
  createDateTo,
  effectiveDateFrom,
  effectiveDateTo,
  entityId,
}: UseAccountingEventExportParams) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const hasAccountingAuditExportFeature = useUserHasFeature(
    Feature.AccountingAuditExport
  );
  const hasAccountingFeedUIFeature = useUserHasFeature(
    Feature.AccountingFeedUI
  );
  const hasFeature =
    hasAccountingAuditExportFeature || hasAccountingFeedUIFeature;

  const downloadStartingMsg = useContent(ContentId.ExportDownloadStarting);
  const exportNoDownloadUrlMsg = useContent(ContentId.ExportNoDownloadUrl);

  const [isExporting, setIsExporting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const triggerDownload = useCallback(
    (downloadUrl: string) => {
      setStatusMessage(downloadStartingMsg);
      downloadFileFromUrl('accounting-export.csv', downloadUrl);
    },
    [downloadStartingMsg]
  );

  const startExport = useCallback(async () => {
    if (!hasFeature || isExporting) return;

    setIsExporting(true);
    setStatusMessage(null);

    const client = new AccountingEventClient(activeAccountWebClientConfig);

    let result: AccountingEventExportResult | null = null;
    try {
      result = await client.createExport({
        stream,
        createDateFrom: createDateFrom ?? null,
        createDateTo: createDateTo ?? null,
        effectiveDateFrom: effectiveDateFrom ?? null,
        effectiveDateTo: effectiveDateTo ?? null,
        entityId: entityId ?? null,
      });
    } catch (err) {
      setIsExporting(false);
      setStatusMessage(null);
      showErrorDialog(
        'AccountingEventClient.createExport',
        err instanceof Error ? err : new Error(String(err)),
        {
          trackErrorData: {
            stream,
            createDateFrom,
            createDateTo,
            effectiveDateFrom,
            effectiveDateTo,
            entityId,
          },
        }
      );
      return;
    }

    setIsExporting(false);

    if (result?.downloadUrl) {
      triggerDownload(result.downloadUrl);
    } else if (result) {
      setStatusMessage(exportNoDownloadUrlMsg);
      setTimeout(() => setStatusMessage(null), 3000);
    }
  }, [
    hasFeature,
    isExporting,
    activeAccountWebClientConfig,
    exportNoDownloadUrlMsg,
    stream,
    createDateFrom,
    createDateTo,
    effectiveDateFrom,
    effectiveDateTo,
    entityId,
    showErrorDialog,
    triggerDownload,
  ]);

  return { startExport, isExporting, statusMessage, hasFeature };
};
